import { DateTime } from 'luxon';
import { Clickable, ClickHandler } from 'dg-web-shared/ui/Clickable.tsx';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import { Icon16 } from '../icons/Icon.tsx';
import {
    getOrElse,
    isDefined,
    isUndefined,
    Maybe,
    thenElse,
} from 'dg-web-shared/lib/MaybeV2.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { range } from 'dg-web-shared/lib/ArrayUtil.ts';
import { iconButtonStyleParkingPortal, MonthPicker } from './MonthPicker.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { css } from '@emotion/css';
import { paper } from 'dg-web-shared/tb-ui/paper.ts';
import { ColorHex } from '../Colors.ts';
import { OperatorTypo } from '../OperatorTypo.ts';
import { useState, type JSX } from 'react';

const Weekday = (p: { day: Message }): JSX.Element => (
    <div
        className={css({
            color: ColorHex.rgba(ColorHex.darkblue, 0.4),
            float: 'left',
            width: '40px',
            height: '40px',
            textAlign: 'center',
            paddingTop: '11px',
        })}
    >
        <Localized {...p.day} />
    </div>
);

const Weekdays = (): JSX.Element => (
    <div
        className={css({
            height: '40px',
            width: '100%',
            background: ColorHex.rgba(ColorHex.lightblue, 0.1),
            position: 'absolute',
            top: '40px',
            ...OperatorTypo.bodyC,
        })}
    >
        <Weekday day={{ de: 'M', fr: 'L', it: 'L', en: 'M' }} />
        <Weekday day={{ de: 'D', fr: 'M', it: 'M', en: 'T' }} />
        <Weekday day={{ de: 'M', fr: 'M', it: 'M', en: 'W' }} />
        <Weekday day={{ de: 'D', fr: 'J', it: 'G', en: 'T' }} />
        <Weekday day={{ de: 'F', fr: 'V', it: 'V', en: 'F' }} />
        <Weekday day={{ de: 'S', fr: 'S', it: 'S', en: 'S' }} />
        <Weekday day={{ de: 'S', fr: 'D', it: 'D', en: 'S' }} />
    </div>
);

interface MonthYearNavigationProps {
    visibleDate: DateTime;
    onPrev: Maybe<ClickHandler>;
    onNext: Maybe<ClickHandler>;
    onCurrent: ClickHandler;
    language: string;
}

const MonthYearNavigation = (p: MonthYearNavigationProps): JSX.Element => {
    return (
        <div
            className={css({
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '40px',
                background: ColorHex.rgba(ColorHex.lightblue, 0.2),
                color: ColorHex.darkblue,
            })}
        >
            <Clickable
                element="div"
                className={iconButtonStyleParkingPortal(
                    isUndefined(p.onPrev),
                    'prev',
                )}
                onClick={p.onPrev}
                disabled={isUndefined(p.onPrev)}
            >
                <Icon16 icon={Icons16.chevronLeft} />
            </Clickable>
            <Clickable
                element="div"
                className={iconButtonStyleParkingPortal(
                    isUndefined(p.onNext),
                    'next',
                )}
                onClick={p.onNext}
                disabled={isUndefined(p.onNext)}
            >
                <Icon16 icon={Icons16.chevronRight} />
            </Clickable>
            <Clickable
                element="div"
                className={css([
                    {
                        margin: '0 40px',
                        textAlign: 'center',
                        height: '100%',
                        paddingTop: '11px',
                        ...OperatorTypo.bodyC,
                    },
                    !isUndefined(p.onCurrent) && {
                        '&:hover': {
                            background: ColorHex.lightblue,
                            color: ColorHex.white,
                        },
                    },
                ])}
                disabled={isUndefined(p.onCurrent)}
                onClick={p.onCurrent}
            >
                {Formatter.monthNameYear(
                    p.visibleDate,
                    Formatter.getLocaleFromString(p.language),
                )}
            </Clickable>
        </div>
    );
};

export type DateSelectHandler = (d: DateTime) => void;

interface CalendarElementProps {
    day: DateTime;
    onSelect: DateSelectHandler;
    currentDate: DateTime;
    selectedDate: Maybe<DateTime>;
    minDate: Maybe<DateTime>;
    maxDate: Maybe<DateTime>;
}

interface DayProps extends CalendarElementProps {
    invalid: boolean;
    inactive: boolean;
}

function Day(props: DayProps) {
    const [hover, setHover] = useState(false);

    function getState(): string {
        if (props.invalid) {
            return 'invalid';
        } else if (props.inactive) {
            return 'inactive';
        } else {
            return 'active';
        }
    }

    const current = props.day.hasSame(props.currentDate, 'day');
    const selected = thenElse(
        props.selectedDate,
        d => props.day.hasSame(d, 'day'),
        false,
    );
    return (
        <Clickable
            element="div"
            className={css([
                {
                    ...OperatorTypo.body,
                    color: ColorHex.darkblue,
                    width: '32px',
                    height: '32px',
                    margin: '4px',
                    float: 'left',
                    textAlign: 'center',
                    borderRadius: '50%',
                    position: 'relative',
                    border: `2px solid ${ColorHex.rgba('#000', 0)}`,
                },
                getState() === 'active' && [
                    hover && {
                        color: ColorHex.white,
                        background: ColorHex.lightblue,
                    },
                    !selected && [
                        current && [
                            {
                                borderColor: ColorHex.darkblue,
                            },
                            hover && {
                                borderColor: ColorHex.lightblue,
                            },
                        ],
                    ],
                    selected && {
                        background: ColorHex.darkblue,
                        color: ColorHex.white,
                    },
                ],
            ])}
            disabled={props.invalid || props.inactive}
            onClick={() => props.onSelect(props.day)}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
        >
            <div
                className={css([
                    {
                        paddingTop: '4px',
                        height: '100%',
                        width: '100%',
                        border: `2px solid ${ColorHex.rgba('#000', 0)}`,
                        borderRadius: '50%',
                    },
                    getState() === 'inactive' && {
                        color: ColorHex.rgba(ColorHex.darkblue, 0.4),
                    },
                    !props.invalid && [
                        current && [
                            hover && {
                                borderColor: ColorHex.white,
                            },
                        ],
                        selected && [
                            { borderColor: ColorHex.darkblue },
                            current && { borderColor: ColorHex.white },
                        ],
                    ],
                ])}
            >
                {props.invalid ? '' : props.day.toFormat('d')}
            </div>
        </Clickable>
    );
}

interface WeekProps extends CalendarElementProps {
    month: DateTime;
}

const Week = (p: WeekProps): JSX.Element => {
    const { minDate, maxDate } = p;
    return (
        <div
            className={css({
                height: '40px',
                width: '100%',
                background: ColorHex.rgba(ColorHex.lightblue, 0.1),
            })}
        >
            {range(7).map(d => {
                const day = p.day.plus({ days: d });

                const outsideRange =
                    (isDefined(minDate) && day < minDate) ||
                    (isDefined(maxDate) && day > maxDate.endOf('day'));

                return (
                    <Day
                        key={day.toISODate()}
                        {...p}
                        day={day}
                        invalid={!day.hasSame(p.month, 'month')}
                        inactive={outsideRange}
                    />
                );
            })}
        </div>
    );
};

type MonthProps = CalendarElementProps;

const Month = (p: MonthProps): JSX.Element => (
    <div
        className={css({
            position: 'absolute',
            top: '80px',
            bottom: 0,
            width: '100%',
        })}
    >
        {range(6)
            .map(w => p.day.startOf('week').plus({ weeks: w }))
            .map(d => (
                <Week key={d.toISODate()} {...p} day={d} month={p.day} />
            ))}
    </div>
);

export interface DatePickerProps {
    currentDate: DateTime;
    selectedDate: Maybe<DateTime>;
    onSelect: DateSelectHandler;
    minDate?: Maybe<DateTime>;
    maxDate?: Maybe<DateTime>;
    language: string;
}

export function DatePicker(props: DatePickerProps) {
    const [visibleDate, setVisibleDate] = useState<DateTime | undefined>(() =>
        getOrElse(props.selectedDate, props.currentDate),
    );
    const [monthPickerVisible, setMonthPickerVisible] = useState(false);

    function renderMonthPicker(): JSX.Element | null {
        if (!monthPickerVisible) {
            return null;
        }
        return (
            <div
                className={css({
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                })}
            >
                <MonthPicker
                    currentDate={DateTime.now()}
                    selectedDate={visibleDate}
                    onSelect={m => {
                        setVisibleDate(m);
                        setMonthPickerVisible(false);
                    }}
                    onClose={() => setMonthPickerVisible(false)}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    language={props.language}
                />
            </div>
        );
    }

    function getPrevMonthHandler(): Maybe<ClickHandler> {
        const { minDate } = props;
        if (
            isDefined(minDate) &&
            visibleDate &&
            visibleDate.startOf('month') < minDate
        ) {
            return null;
        } else {
            return () => {
                if (!visibleDate) {
                    return;
                }

                setVisibleDate(visibleDate.minus({ months: 1 }));
            };
        }
    }

    function getNextMonthHandler(): Maybe<ClickHandler> {
        const { maxDate } = props;
        if (
            isDefined(maxDate) &&
            visibleDate &&
            visibleDate.endOf('month') > maxDate
        ) {
            return null;
        } else {
            return () => {
                if (!visibleDate) {
                    return;
                }

                setVisibleDate(visibleDate.plus({ months: 1 }));
            };
        }
    }

    return (
        <div
            className={css({
                height: '320px',
                width: '280px',
                position: 'relative',
                ...paper(3),
            })}
        >
            <MonthYearNavigation
                visibleDate={visibleDate || DateTime.now()}
                onPrev={getPrevMonthHandler()}
                onNext={getNextMonthHandler()}
                onCurrent={() => setMonthPickerVisible(true)}
                language={props.language}
            />
            <Weekdays />
            <Month
                currentDate={props.currentDate}
                selectedDate={props.selectedDate}
                day={
                    visibleDate ? visibleDate.startOf('month') : DateTime.now()
                }
                onSelect={props.onSelect}
                minDate={props.minDate}
                maxDate={props.maxDate}
            />
            {renderMonthPicker()}
        </div>
    );
}
